import { ApplicationState } from '@app/store'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { createSelector } from 'reselect'

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface IReactiveLayoutState {
  open: boolean
  sidebarReady: boolean
  panning: boolean
  touchEnabled: boolean
  tx: number
  headerHeight: number
  sidebarWidth: number
  touch: any
  lx: number
}

const ReactiveLayoutStateRecord = Record({
  headerHeight: 54,
  lx: 0,
  open: false,
  panning: false,
  sidebarReady: false,
  sidebarWidth: 0,
  touch: undefined,
  touchEnabled: true,
  tx: 0,
})

export class ReactiveLayoutState extends ReactiveLayoutStateRecord
  implements IReactiveLayoutState {
  public static fromJS(initialState?: Partial<IReactiveLayoutState>) {
    return new ReactiveLayoutState(initialState)
  }

  public readonly open: boolean
  public readonly sidebarReady: boolean
  public readonly panning: boolean
  public readonly touchEnabled: boolean
  public readonly tx: number
  public readonly headerHeight: number
  public readonly sidebarWidth: number
  public readonly touch: any
  public readonly lx: number

  constructor(props?: Partial<IReactiveLayoutState>) {
    props ? super(props) : super()
  }
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.
export enum ReactiveLayoutActionTypes {
  InitializeLayout = 'REACTIVE_LAYOUT::INITIALIZE_LAYOUT',
  ToggleSidebar = 'REACTIVE_LAYOUT::TOGGLE_SIDEBAR',
  TouchStart = 'REACTIVE_LAYOUT::TOUCH_START',
  TouchMove = 'REACTIVE_LAYOUT::TOUCH_MOVE',
  TouchEnd = 'REACTIVE_LAYOUT::TOUCH_END',
  SetTouchEnabled = 'REACTIVE_LAYOUT::SET_TOUCH_ENABLED',
}

interface IInitializeLayoutAction {
  type: ReactiveLayoutActionTypes.InitializeLayout
  headerHeight: number
  sidebarWidth: number
  tx: number
  sidebarReady: boolean
}
interface IToggleSidebarAction {
  type: ReactiveLayoutActionTypes.ToggleSidebar
}
interface ITouchStartAction {
  type: ReactiveLayoutActionTypes.TouchStart
  touchObject: any
}
interface ITouchMoveAction {
  type: ReactiveLayoutActionTypes.TouchMove
  touchObject: any
}
interface ITouchEndAction {
  type: ReactiveLayoutActionTypes.TouchEnd
  touchObject: any
}
interface ITouchEnabledAction {
  type: ReactiveLayoutActionTypes.SetTouchEnabled
  enabled: boolean
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IInitializeLayoutAction
  | IToggleSidebarAction
  | ITouchStartAction
  | ITouchMoveAction
  | ITouchEndAction
  | ITouchEnabledAction

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
export const actionCreators = {
  initializeLayout: (
    headerHeight: number,
    sidebarWidth: number,
    tx: number,
    sidebarReady: boolean,
  ): IInitializeLayoutAction => ({
    headerHeight,
    sidebarReady,
    sidebarWidth,
    tx,
    type: ReactiveLayoutActionTypes.InitializeLayout,
  }),
  setTouchEnabled: (enabled: boolean): ITouchEnabledAction => ({
    enabled,
    type: ReactiveLayoutActionTypes.SetTouchEnabled,
  }),
  toggleSidebar: (): IToggleSidebarAction => ({
    type: ReactiveLayoutActionTypes.ToggleSidebar,
  }),
  touchEnd: (touchObject: any): ITouchEndAction => ({
    touchObject,
    type: ReactiveLayoutActionTypes.TouchEnd,
  }),
  touchMove: (touchObject: any): ITouchMoveAction => ({
    touchObject,
    type: ReactiveLayoutActionTypes.TouchMove,
  }),
  touchStart: (touchObject: any): ITouchStartAction => ({
    touchObject,
    type: ReactiveLayoutActionTypes.TouchStart,
  }),
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<ReactiveLayoutState> = (
  state: ReactiveLayoutState = new ReactiveLayoutState(),
  action: KnownAction,
) => {
  switch (action.type) {
    case ReactiveLayoutActionTypes.InitializeLayout:
      return state
        .set('headerHeight', action.headerHeight)
        .set('sidebarWidth', action.sidebarWidth)
        .set('tx', action.tx)
        .set('sidebarReady', action.sidebarReady) as ReactiveLayoutState
    case ReactiveLayoutActionTypes.ToggleSidebar:
      if (state.touchEnabled === true) {
        if (state.open) {
          return state
            .set('tx', -state.sidebarWidth)
            .set('open', false) as ReactiveLayoutState
        } else {
          return state.set('tx', 0).set('open', true) as ReactiveLayoutState
        }
      } else {
        return state
      }
    case ReactiveLayoutActionTypes.TouchStart:
      if (!state.touchEnabled) {
        return state as ReactiveLayoutState
      }

      if (!state.touch) {
        return state.set('touch', {
          start: new Date().getTime(),
          x0: action.touchObject.clientX,
          y0: action.touchObject.clientY,
        }) as ReactiveLayoutState
      }

      return state as ReactiveLayoutState
    case ReactiveLayoutActionTypes.TouchMove:
      if (!state.touch || !state.touchEnabled) {
        return state as ReactiveLayoutState
      }

      const tempTouch = state.touch

      const now = new Date().getTime()
      const elapsed = now - state.touch.start
      const dx = action.touchObject.clientX - state.touch.x0
      const dy = action.touchObject.clientY - state.touch.y0
      const vx = dx / elapsed
      const vy = dy / elapsed

      if (tempTouch.pan) {
        if (state.touch.lx) {
          tempTouch.vx =
            (action.touchObject.clientX - tempTouch.lx) / (now - tempTouch.lt)
        }

        tempTouch.lt = now
        tempTouch.lx = action.touchObject.clientX

        const boundedX = Math.max(
          -state.sidebarWidth,
          Math.min(0, tempTouch.sx + dx),
        )

        return state
          .set('touch', tempTouch)
          .set('sidebarReady', true)
          .set('tx', boundedX)
          .set('lx', dx)
          .set('panning', true) as ReactiveLayoutState
      } else {
        if (Math.abs(vx) > 0.1 && Math.abs(vy) < 0.25) {
          tempTouch.sx = state.tx
          tempTouch.pan = true

          return state.set('touch', tempTouch) as ReactiveLayoutState
        } else {
          return state as ReactiveLayoutState
        }
      }
    case ReactiveLayoutActionTypes.TouchEnd:
      if (!state.touchEnabled) {
        return state as ReactiveLayoutState
      }

      if (state.touch && state.touch.pan) {
        if (state.touch.vx > 0) {
          return state
            .set('tx', 0)
            .set('panning', false)
            .set('open', true)
            .set('touch', undefined) as ReactiveLayoutState
        } else {
          return state
            .set('tx', -state.sidebarWidth)
            .set('panning', false)
            .set('open', false)
            .set('touch', undefined) as ReactiveLayoutState
        }
      }

      return state.set('touch', undefined) as ReactiveLayoutState
    case ReactiveLayoutActionTypes.SetTouchEnabled:
      return state
        .set('open', !action.enabled)
        .set('tx', action.enabled ? -state.sidebarWidth : state.tx)
        .set('touchEnabled', action.enabled) as ReactiveLayoutState
    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  // For unrecognized actions (or in cases where actions have no effect), must return the existing state
  //  (or default initial state if none was supplied)
  return state
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.reactiveLayout

export const selectReactiveLayoutState = () =>
  createSelector(
    selectModuleState(),
    (mState) => {
      return {
        headerHeight: mState.headerHeight,
        lx: mState.lx,
        open: mState.open,
        panning: mState.panning,
        sidebarReady: mState.sidebarReady,
        sidebarWidth: mState.sidebarWidth,
        touch: mState.touch,
        touchEnabled: mState.touchEnabled,
        tx: mState.tx,
      } as IReactiveLayoutState
    },
  )
